import { X } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';

import { TeachPhoebeWordmark } from '@apps/web/components/page_tour/page_tour_logos';
import {
    isPageTourSeen,
    markPageTourSeen,
} from '@apps/web/components/page_tour/page_tour_storage';
import { Button } from '@apps/web/components/ui/button';
import {
    Popover,
    PopoverAnchor,
    PopoverContent,
} from '@apps/web/components/ui/popover';

export type PageTourStep = {
    // Anchor candidates in priority order; the first one rendered wins. Later
    // entries keep the step alive when the preferred target is not on screen.
    // When highlightAllTargets is true, every listed target that is rendered
    // gets a highlight ring.
    readonly targetTestIds: ReadonlyArray<string>;
    readonly highlightAllTargets?: boolean;
    readonly body: string;
    readonly popoverSide?: 'top' | 'right' | 'bottom' | 'left';
} & (
    | { readonly title: string; readonly titleLogo?: never }
    | { readonly titleLogo: 'teach-phoebe'; readonly title?: never }
);

function PageTourStepTitle({ step }: { readonly step: PageTourStep }) {
    if (step.titleLogo === 'teach-phoebe') {
        return <TeachPhoebeWordmark />;
    }

    return <p className="pr-6 text-sm font-semibold">{step.title}</p>;
}

function findTourStepTarget(step: PageTourStep): HTMLElement | null {
    for (const targetTestId of step.targetTestIds) {
        const element = document.querySelector<HTMLElement>(
            `[data-testid="${targetTestId}"]`,
        );
        if (element !== null) {
            return element;
        }
    }
    return null;
}

function findTourStepTargets(step: PageTourStep): ReadonlyArray<HTMLElement> {
    if (step.highlightAllTargets === true) {
        return step.targetTestIds
            .map((targetTestId) =>
                document.querySelector<HTMLElement>(
                    `[data-testid="${targetTestId}"]`,
                ),
            )
            .filter((element): element is HTMLElement => element !== null);
    }

    const element = findTourStepTarget(step);
    return element === null ? [] : [element];
}

/**
 * One-time guided walkthrough anchored to on-screen elements by test id.
 * Steps whose target is not currently rendered are skipped. Any dismissal —
 * finishing, dismissing with the X button or Escape, or clicking outside —
 * marks the tour seen in localStorage so it never reappears for that user/scope.
 */
export function PageTour({
    ready,
    userId,
    seenStorageKey,
    steps,
    testIdPrefix = 'page-tour',
    replayToken = 0,
    popoverSide,
}: {
    readonly ready: boolean;
    readonly userId: string | null;
    readonly seenStorageKey: string | null;
    readonly steps: ReadonlyArray<PageTourStep>;
    readonly testIdPrefix?: string;
    readonly replayToken?: number;
    readonly popoverSide?: 'top' | 'right' | 'bottom' | 'left';
}) {
    // null = tour closed; otherwise the index into steps.
    const [stepIndex, setStepIndex] = useState<number | null>(null);
    const [anchorElement, setAnchorElement] = useState<HTMLElement | null>(
        null,
    );
    const [highlightRects, setHighlightRects] = useState<
        ReadonlyArray<DOMRect>
    >([]);
    const [resolveNonce, setResolveNonce] = useState(0);
    // Each replay click bumps replayToken; consume it once so re-runs of the
    // start effect (e.g. `ready` toggling on data refetches) don't reopen a
    // tour the user already finished or dismissed.
    const handledReplayTokenRef = useRef(replayToken);

    useEffect(() => {
        if (!ready || userId === null || seenStorageKey === null) {
            return;
        }
        if (replayToken > handledReplayTokenRef.current) {
            handledReplayTokenRef.current = replayToken;
            setStepIndex(0);
            setResolveNonce((nonce) => nonce + 1);
            return;
        }
        if (isPageTourSeen(seenStorageKey)) {
            return;
        }
        setStepIndex((current) => current ?? 0);
    }, [ready, replayToken, seenStorageKey, userId]);

    // Resolve the current step's anchor after render so conditionally
    // rendered targets are found; missing targets skip the step forward.
    // resolveNonce re-runs the lookup when the current anchor unmounts (e.g.
    // the user switched views mid-tour).
    useEffect(() => {
        const currentStep = stepIndex === null ? undefined : steps[stepIndex];
        if (
            stepIndex === null ||
            currentStep === undefined ||
            seenStorageKey === null
        ) {
            setAnchorElement(null);
            setHighlightRects([]);
            return;
        }
        const elements = findTourStepTargets(currentStep);
        const firstElement = elements[0];
        if (firstElement === undefined) {
            if (stepIndex + 1 < steps.length) {
                setStepIndex(stepIndex + 1);
            } else {
                markPageTourSeen(seenStorageKey);
                setStepIndex(null);
            }
            return;
        }
        firstElement.scrollIntoView({ block: 'nearest', behavior: 'instant' });
        setAnchorElement(firstElement);
        setHighlightRects(
            elements.map((element) => element.getBoundingClientRect()),
        );
    }, [seenStorageKey, stepIndex, steps, resolveNonce]);

    // Re-anchor when the current target leaves the DOM so the popover never
    // floats at a stale position after the page re-renders around it.
    useEffect(() => {
        if (anchorElement === null) {
            return;
        }
        const observer = new MutationObserver(() => {
            if (!anchorElement.isConnected) {
                setResolveNonce((nonce) => nonce + 1);
            }
        });
        observer.observe(document.body, { childList: true, subtree: true });
        return () => observer.disconnect();
    }, [anchorElement]);

    useEffect(() => {
        if (anchorElement === null) {
            setHighlightRects([]);
            return;
        }
        function measure(): void {
            if (anchorElement === null) {
                return;
            }
            const currentStep =
                stepIndex === null ? undefined : steps[stepIndex];
            if (currentStep === undefined) {
                setHighlightRects([]);
                return;
            }
            const elements = findTourStepTargets(currentStep);
            setHighlightRects(
                elements.map((element) => element.getBoundingClientRect()),
            );
        }
        measure();
        window.addEventListener('resize', measure);
        window.addEventListener('scroll', measure, true);
        return () => {
            window.removeEventListener('resize', measure);
            window.removeEventListener('scroll', measure, true);
        };
    }, [anchorElement, stepIndex, steps]);

    const step = stepIndex === null ? undefined : steps[stepIndex];
    if (
        stepIndex === null ||
        step === undefined ||
        seenStorageKey === null ||
        anchorElement === null
    ) {
        return null;
    }

    const isLastStep = stepIndex === steps.length - 1;
    const tourSeenStorageKey = seenStorageKey;

    function finish(): void {
        markPageTourSeen(tourSeenStorageKey);
        setStepIndex(null);
    }

    return (
        <Popover open={true}>
            {highlightRects.map((highlightRect, index) => (
                <div
                    key={index}
                    aria-hidden={true}
                    className="ring-primary pointer-events-none fixed z-50 rounded-md ring-2 ring-offset-2"
                    style={{
                        top: highlightRect.top,
                        left: highlightRect.left,
                        width: highlightRect.width,
                        height: highlightRect.height,
                    }}
                />
            ))}
            <PopoverAnchor virtualRef={{ current: anchorElement }} />
            <PopoverContent
                data-testid={`${testIdPrefix}-popover`}
                align="start"
                side={step.popoverSide ?? popoverSide}
                sideOffset={10}
                collisionPadding={8}
                onCloseAutoFocus={(event) => {
                    // The anchor is virtual, so Radix would move focus to the
                    // body; leave focus where the user left it.
                    event.preventDefault();
                }}
                onInteractOutside={(event) => {
                    // Clicking around the page mid-tour (often to try what a
                    // step describes) must not permanently dismiss the tour;
                    // only Skip, Done, or Escape close it.
                    event.preventDefault();
                }}
                onEscapeKeyDown={() => {
                    finish();
                }}
                className="w-80 space-y-3"
            >
                <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className="text-muted-foreground absolute top-2 right-2 size-6"
                    aria-label="Dismiss tour"
                    data-testid={`${testIdPrefix}-dismiss`}
                    onClick={finish}
                >
                    <X className="size-4" />
                </Button>
                <div className="space-y-1">
                    <PageTourStepTitle step={step} />
                    <p className="text-muted-foreground text-sm">{step.body}</p>
                </div>
                <div className="flex items-center justify-end gap-2">
                    {stepIndex > 0 ? (
                        <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            onClick={() => setStepIndex(stepIndex - 1)}
                        >
                            Back
                        </Button>
                    ) : null}
                    <Button
                        type="button"
                        size="sm"
                        data-testid={`${testIdPrefix}-next`}
                        onClick={() => {
                            if (isLastStep) {
                                finish();
                            } else {
                                setStepIndex(stepIndex + 1);
                            }
                        }}
                    >
                        {isLastStep ? 'Done' : 'Next'}
                    </Button>
                </div>
            </PopoverContent>
        </Popover>
    );
}
